import * as moment from 'moment';

import { Action, State, StateContext } from '@ngxs/store';
import { map, tap } from 'rxjs/operators';
import {
  GetCftDocuments,
  GetCftDocumentsSuccess,
  GetGroups,
  GetGroupsSuccess,
  GetGroupTrainingLoadAnalyses,
  GetGroupTrainingLoadAnalysesSuccess,
  GetGroupTrainingLoads,
  GetGroupTrainingLoadsSuccess,
  GetIndividualCftScores,
  GetIndividualCftScoresSuccess,
  GetIndividualPftScores,
  GetIndividualPftScoresSuccess,
  GetIndividualTrainingLoadAnalyses,
  GetIndividualTrainingLoadAnalysesSuccess,
  GetIndividualTrainingLoads,
  GetIndividualTrainingLoadsSuccess,
  GetMarines,
  GetPftDocuments,
  GetPftDocumentsSuccess,
  GetPlansPublishedToAllGroups,
  GetPlansPublishedToAllGroupsSuccess,
  GetPlansPublishedToGroup,
  GetPlansPublishedToGroupSuccess,
  GetPlanTrainingLoadAnalyses,
  GetPlanTrainingLoadAnalysesSuccess,
  GetPlanTrainingLoads,
  GetPlanTrainingLoadsSuccess,
  GetTrainingLoadParams,
  GetTrainingLoadParamsSuccess,
  GetUserData,
  GetUserDataSuccess,
  ResetExploreState,
  SelectGroup,
  SelectMarine,
  SelectPlan,
  SetTabState,
} from './explore.actions';

import { Injectable } from '@angular/core';
import { Group } from 'app/group-management/models/group.model';
import { flatMap } from 'lodash';
import { forkJoin } from 'rxjs';
import { ExploreStateModel } from '../models/explore-state.model';
import { TrainingLoadParams } from '../models/training-load-params.model';
import { DataService } from '../services/data.service';

const defaults = {
  loading: false,
  marines: [],
  groups: [],
  groupTrainingLoads: [],
  plans: [],
  rangeStart: undefined,
  rangeEnd: undefined,
  weeks: [],
  selectedMarine: undefined,
  selectedGroupPlans: [],
  tabState: undefined,
  individualTrainingLoads: [],
  individualTrainingLoadAnalyses: [],
  planTrainingLoadAnalyses: [],
  groupTrainingLoadAnalyses: [],
  planTrainingLoads: [],
  trainingLoadParams: [],
  individualPftScoreDocuments: [],
  individualCftScoreDocuments: [],
  groupPftScoreDocuments: [],
  groupCftScoreDocuments: [],
} as ExploreStateModel;

@State<ExploreStateModel>({
  name: 'explore',
  defaults: {
    ...defaults,
  },
})
@Injectable()
export class ExploreState {
  constructor(private dataService: DataService) {}

  @Action(GetGroups)
  getGroups(ctx: StateContext<ExploreStateModel>) {
    ctx.setState({
      ...defaults,
      loading: true,
    });

    return this.dataService.getGroups().pipe(
      tap(groups => {
        ctx.dispatch(new GetGroupsSuccess(groups));
        ctx.dispatch(new GetMarines(groups));
      }),
    );
  }

  @Action(GetMarines)
  getMarines(ctx: StateContext<ExploreStateModel>, action: GetMarines) {
    const state = ctx.getState();
    const groups = action.groups.slice().sort((a, b) => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1));

    const idSet = new Set<string>();
    const marines = flatMap(groups, (group: Group) => {
      return group.members.filter(member => {
        if (!idSet.has(member.uuid)) {
          idSet.add(member.uuid);
          return member;
        }
      });
    }).sort((a, b) => {
      const aName = (a.firstName + '_' + a.lastName).toLowerCase();
      const bName = (b.firstName + '_' + b.lastName).toLowerCase();
      return aName < bName ? -1 : aName > bName ? 1 : 0;
    });

    ctx.setState({
      ...state,
      marines: marines,
    });
  }

  @Action(GetGroupsSuccess)
  getGroupsSuccess(ctx: StateContext<ExploreStateModel>, action: GetGroupsSuccess) {
    const state = ctx.getState();
    ctx.setState({
      ...state,
      groups: action.groups,
      loading: false,
    });
  }

  @Action(SelectMarine)
  selectMarine(ctx: StateContext<ExploreStateModel>, action: SelectMarine) {
    const state = ctx.getState();
    const selectedMarine = state.marines.find(m => m.uuid === action.uuid);
    ctx.setState({
      ...state,
      selectedMarine: selectedMarine,
      individualTrainingLoads: [],
      individualTrainingLoadAnalyses: [],
    });
  }

  @Action(SelectPlan)
  selectPlan(ctx: StateContext<ExploreStateModel>, action: SelectPlan) {
    const state = ctx.getState();
    ctx.setState({
      ...state,
      selectedPlan: action.plan,
      planTrainingLoads: [],
      planTrainingLoadAnalyses: [],
    });
  }

  @Action(SelectGroup)
  selectGroup(ctx: StateContext<ExploreStateModel>, action: SelectGroup) {
    const state = ctx.getState();
    ctx.setState({
      ...state,
      selectedGroup: action.group,
      groupTrainingLoads: [],
      groupTrainingLoadAnalyses: [],
    });
  }

  @Action(GetGroupTrainingLoads)
  getGroupTrainingLoads(ctx: StateContext<ExploreStateModel>, action: GetGroupTrainingLoads) {
    const state = ctx.getState();
    ctx.setState({
      ...state,
      loadingTrainingLoads: true,
    });

    return this.dataService
      .getGroupTrainingLoads(action.group.uuid, action.startDate, action.endDate)
      .pipe(tap(trainingLoads => ctx.dispatch(new GetGroupTrainingLoadsSuccess(trainingLoads))));
  }

  @Action(GetGroupTrainingLoadsSuccess)
  getGroupTrainingLoadsSuccess(ctx: StateContext<ExploreStateModel>, action: GetGroupTrainingLoadsSuccess) {
    const state = ctx.getState();
    ctx.setState({
      ...state,
      loadingTrainingLoads: false,
      groupTrainingLoads: action.trainingLoads,
    });
  }

  @Action(GetIndividualTrainingLoads)
  getIndividualTrainingLoads(ctx: StateContext<ExploreStateModel>, action: GetIndividualTrainingLoads) {
    const state = ctx.getState();
    ctx.setState({
      ...state,
      loadingTrainingLoads: true,
    });

    return this.dataService
      .getIndividualTrainingLoads(action.marine.uuid, action.startDate, action.endDate)
      .pipe(tap(trainingLoads => ctx.dispatch(new GetIndividualTrainingLoadsSuccess(trainingLoads))));
  }

  @Action(GetIndividualTrainingLoadsSuccess)
  getIndividualTrainingLoadsSuccess(ctx: StateContext<ExploreStateModel>, action: GetIndividualTrainingLoadsSuccess) {
    const state = ctx.getState();
    ctx.setState({
      ...state,
      loadingTrainingLoads: false,
      individualTrainingLoads: action.trainingLoads,
    });
  }

  @Action(GetIndividualTrainingLoadAnalyses)
  getIndividualTrainingLoadAnalyses(ctx: StateContext<ExploreStateModel>, action: GetIndividualTrainingLoadAnalyses) {
    const state = ctx.getState();
    ctx.setState({
      ...state,
      loadingAnalyses: true,
    });

    return this.dataService
      .getIndividualTrainingLoadAnalyses(action.marine.uuid, action.startDate, action.endDate)
      .pipe(tap(trainingLoads => ctx.dispatch(new GetIndividualTrainingLoadAnalysesSuccess(trainingLoads))));
  }

  @Action(GetIndividualTrainingLoadAnalysesSuccess)
  getIndividualTrainingLoadAnalysesSuccess(ctx: StateContext<ExploreStateModel>, action: GetIndividualTrainingLoadAnalysesSuccess) {
    const state = ctx.getState();
    ctx.setState({
      ...state,
      loadingAnalyses: false,
      individualTrainingLoadAnalyses: action.trainingLoads,
    });
  }

  @Action(GetPlanTrainingLoads)
  getPlanTrainingLoads(ctx: StateContext<ExploreStateModel>, action: GetPlanTrainingLoads) {
    ctx.setState({
      ...ctx.getState(),
      loadingTrainingLoads: true,
    });

    return this.dataService
      .getPlanTrainingLoads(action.plan.uuid, action.startDate, action.endDate)
      .pipe(tap(trainingLoads => ctx.dispatch(new GetPlanTrainingLoadsSuccess(trainingLoads))));
  }

  @Action(GetPlanTrainingLoadsSuccess)
  getPlanTrainingLoadsSuccess(ctx: StateContext<ExploreStateModel>, action: GetPlanTrainingLoadsSuccess) {
    ctx.setState({
      ...ctx.getState(),
      loadingTrainingLoads: false,
      planTrainingLoads: action.trainingLoads,
    });
  }

  @Action(GetPlanTrainingLoadAnalyses)
  getPlanTrainingLoadAnalyses(ctx: StateContext<ExploreStateModel>, action: GetPlanTrainingLoadAnalyses) {
    const state = ctx.getState();
    ctx.setState({
      ...state,
      loadingAnalyses: true,
    });

    return this.dataService
      .getPlanTrainingLoadAnalyses(action.plan.uuid, action.startDate, action.endDate)
      .pipe(tap(trainingLoads => ctx.dispatch(new GetIndividualTrainingLoadAnalysesSuccess(trainingLoads))));
  }

  @Action(GetPlanTrainingLoadAnalysesSuccess)
  GetPlanTrainingLoadAnalysesSuccess(ctx: StateContext<ExploreStateModel>, action: GetPlanTrainingLoadAnalysesSuccess) {
    const state = ctx.getState();
    ctx.setState({
      ...state,
      loadingAnalyses: false,
      planTrainingLoadAnalyses: action.trainingLoads,
    });
  }

  @Action(GetGroupTrainingLoadAnalyses)
  getGroupTrainingLoadAnalyses(ctx: StateContext<ExploreStateModel>, action: GetGroupTrainingLoadAnalyses) {
    const state = ctx.getState();
    ctx.setState({
      ...state,
      loadingAnalyses: true,
    });

    return this.dataService
      .getGroupTrainingLoadAnalyses(action.group.uuid, action.startDate, action.endDate)
      .pipe(tap(trainingLoads => ctx.dispatch(new GetIndividualTrainingLoadAnalysesSuccess(trainingLoads))));
  }

  @Action(GetGroupTrainingLoadAnalysesSuccess)
  GetGroupTrainingLoadAnalysesSuccess(ctx: StateContext<ExploreStateModel>, action: GetGroupTrainingLoadAnalysesSuccess) {
    const state = ctx.getState();
    ctx.setState({
      ...state,
      loadingAnalyses: false,
      groupTrainingLoadAnalyses: action.trainingLoads,
    });
  }

  @Action(GetPlansPublishedToAllGroups)
  getPlansPublishedToAllGroups(ctx: StateContext<ExploreStateModel>, action: GetPlansPublishedToAllGroups) {
    return forkJoin(
      action.groupUUIDs.map(uuid => {
        return this.dataService.getPlansMetadata(uuid);
      }),
    ).pipe(
      map(arr => {
        const toRet = [];
        arr.forEach(el => {
          toRet.push(...el);
        });
        return toRet;
      }),
      tap(plans => ctx.dispatch(new GetPlansPublishedToAllGroupsSuccess(plans))),
    );
  }

  @Action(GetPlansPublishedToGroup)
  getPlansPublishedToGroup(ctx: StateContext<ExploreStateModel>, action: GetPlansPublishedToGroup) {
    return this.dataService.getPlansMetadata(action.groupUUID).pipe(tap(plans => ctx.dispatch(new GetPlansPublishedToGroupSuccess(plans))));
  }

  @Action(GetPlansPublishedToGroupSuccess)
  getPlansPublishedToGroupSuccess(ctx: StateContext<ExploreStateModel>, action: GetPlansPublishedToGroupSuccess) {
    const state = ctx.getState();
    let start: moment.Moment;
    let end: moment.Moment;
    const weeks = [];

    if (action.plans.length > 0) {
      action.plans.forEach(plan => {
        const startDate = moment(plan.startDate);
        const endDate = startDate.clone().add(plan.numWeeks, 'w');

        if (!start || startDate.isBefore(start)) {
          start = startDate;
        }

        if (!end || endDate.isAfter(end)) {
          end = endDate;
        }
      });

      const date = start.clone();
      // get first day of week (starts on M)
      while (date.day() > 1) {
        date.subtract(1, 'd');
      }

      // interpolate weeks from range
      while (date.isSameOrBefore(end)) {
        weeks.push({ start: date.clone(), end: date.clone().add(6, 'd'), weekNumber: date.week() });
        date.add(1, 'w');
      }
    }

    ctx.setState({
      ...state,
      selectedGroupPlans: action.plans,
      rangeStart: start,
      rangeEnd: end,
      weeks,
    });
  }

  @Action(GetPlansPublishedToAllGroupsSuccess)
  getPlansPublishedToAllGroupsSuccess(ctx: StateContext<ExploreStateModel>, action: GetPlansPublishedToGroupSuccess) {
    const state = ctx.getState();
    let start: moment.Moment;
    let end: moment.Moment;
    const weeks = [];

    if (action.plans.length > 0) {
      action.plans.forEach(plan => {
        const startDate = moment(plan.startDate);
        const endDate = startDate.clone().add(plan.numWeeks, 'w');

        if (!start || startDate.isBefore(start)) {
          start = startDate;
        }

        if (!end || endDate.isAfter(end)) {
          end = endDate;
        }
      });

      const date = start.clone();
      // get first day of week (starts on M)
      while (date.day() > 1) {
        date.subtract(1, 'd');
      }

      // interpolate weeks from range
      while (date.isSameOrBefore(end)) {
        weeks.push({ start: date.clone(), end: date.clone().add(6, 'd'), weekNumber: date.week() });
        date.add(1, 'w');
      }
    }

    ctx.setState({
      ...state,
      plans: action.plans,
      rangeStart: start,
      rangeEnd: end,
      weeks,
    });
  }

  @Action(GetUserData)
  getUserData(ctx: StateContext<ExploreStateModel>, action: GetUserData) {
    return this.dataService.getUserData(action.marineUUID).pipe(tap(user => ctx.dispatch(new GetUserDataSuccess(user))));
  }

  @Action(GetUserDataSuccess)
  getUserDataSuccess(ctx: StateContext<ExploreStateModel>, action: GetUserDataSuccess) {
    ctx.setState({
      ...ctx.getState(),
      selectedMarine: action.marine,
    });
  }

  @Action(SetTabState)
  setTabState(ctx: StateContext<ExploreStateModel>, action: SetTabState) {
    ctx.setState({
      ...ctx.getState(),
      tabState: action.tabState,
    });
  }

  @Action(GetTrainingLoadParams)
  getTrainingLoadParams(ctx: StateContext<ExploreStateModel>, action: GetTrainingLoadParams) {
    ctx.patchState({
      loading: true,
    });
    return this.dataService
      .getTrainingLoadParams(action.UUID, action.type)
      .pipe(tap(params => ctx.dispatch(new GetTrainingLoadParamsSuccess(params))));
  }

  @Action(GetTrainingLoadParamsSuccess)
  getTrainingLoadParamsSuccess(ctx: StateContext<ExploreStateModel>, action: GetTrainingLoadParamsSuccess) {
    const data = action.trainingLoadParams;
    const paramsArray: TrainingLoadParams[] = [];
    const weekRange = 12;

    const startDate = moment(data.startDate).startOf('week');
    const endDate = moment(data.endDate).endOf('week').startOf('day');

    // calculate how many weeks dont fit into a perfect weekRange-sized chunk
    const remainderWeeks = endDate.diff(startDate, 'weeks') % weekRange;
    const remainderDays = endDate.diff(startDate, 'days') % 7;
    const remainder = remainderDays + remainderWeeks * 7;

    // push the small chunk first
    if (remainder > 0) {
      paramsArray.push({
        startDate: startDate.clone(),
        endDate: startDate.clone().add(remainder, 'd'),
        startWeek: startDate.week(),
        endWeek: startDate.clone().add(remainder, 'd').week(),
        startYear: startDate.year(),
        endYear: startDate.clone().add(remainder, 'd').year(),
      } as TrainingLoadParams);
      startDate.add(remainder, 'd');
    }

    while (startDate.isBefore(endDate)) {
      // if date range to add is 12 full weeks
      paramsArray.push({
        startDate: startDate.clone(),
        endDate: startDate.clone().add(weekRange, 'w'),
        startWeek: startDate.week(),
        endWeek: startDate.clone().add(weekRange, 'w').week(),
        startYear: startDate.year(),
        endYear: startDate.clone().add(weekRange, 'w').year(),
      } as TrainingLoadParams);

      startDate.add(weekRange, 'weeks');
    }
    const state = ctx.getState();
    ctx.patchState({
      ...state,
      loading: false,
      trainingLoadParams: [...paramsArray],
    });
  }

  @Action(ResetExploreState)
  resetExploreState(ctx: StateContext<ExploreStateModel>): void {
    ctx.setState({
      ...defaults,
    });
  }

  @Action(GetPftDocuments)
  getPftDocuments(ctx: StateContext<ExploreStateModel>, action: GetPftDocuments) {
    ctx.patchState({ loadingPftScores: true });
    return this.dataService.getGroupPftDocuments(action.groupUuid).pipe(
      tap(result => {
        ctx.dispatch(new GetPftDocumentsSuccess(result));
      }),
    );
  }

  @Action(GetPftDocumentsSuccess)
  getPftDocumentsSuccess(ctx: StateContext<ExploreStateModel>, action: GetPftDocumentsSuccess) {
    ctx.patchState({
      groupPftScoreDocuments: action.pftDocuments,
      loadingPftScores: false,
    });
  }

  @Action(GetIndividualPftScores)
  getIndividualPftScores(ctx: StateContext<ExploreStateModel>, action: GetIndividualPftScores) {
    ctx.patchState({ loadingPftScores: true });
    return this.dataService.getIndividualPftDocuments(action.marine.uuid).pipe(
      tap(result => {
        ctx.dispatch(new GetIndividualPftScoresSuccess(result));
      }),
    );
  }

  @Action(GetIndividualPftScoresSuccess)
  getIndividualPftScoresSuccess(ctx: StateContext<ExploreStateModel>, action: GetIndividualPftScoresSuccess) {
    ctx.patchState({
      individualPftScoreDocuments: action.pftDocuments,
      loadingPftScores: false,
    });
  }

  @Action(GetCftDocuments)
  getCftDocuments(ctx: StateContext<ExploreStateModel>, action: GetCftDocuments) {
    ctx.patchState({ loadingCftScores: true });
    return this.dataService.getGroupCftDocuments(action.groupUuid).pipe(
      tap(result => {
        ctx.dispatch(new GetCftDocumentsSuccess(result));
      }),
    );
  }

  @Action(GetCftDocumentsSuccess)
  getCftDocumentsSuccess(ctx: StateContext<ExploreStateModel>, action: GetCftDocumentsSuccess) {
    ctx.patchState({
      groupCftScoreDocuments: action.cftDocuments,
      loadingCftScores: false,
    });
  }

  @Action(GetIndividualCftScores)
  getIndividualCftScores(ctx: StateContext<ExploreStateModel>, action: GetIndividualCftScores) {
    ctx.patchState({ loadingCftScores: true });
    return this.dataService.getIndividualCftDocuments(action.marine.uuid).pipe(
      tap(result => {
        ctx.dispatch(new GetIndividualCftScoresSuccess(result));
      }),
    );
  }

  @Action(GetIndividualCftScoresSuccess)
  getIndividualCftScoresSuccess(ctx: StateContext<ExploreStateModel>, action: GetIndividualCftScoresSuccess) {
    ctx.patchState({
      individualCftScoreDocuments: action.cftDocuments,
      loadingCftScores: false,
    });
  }
}
